import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, MailCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";

function formatApiErrorDetail(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
}

export function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Login failed"));
    } finally { setLoading(false); }
  };

  return <AuthShell title="Welcome back" subtitle="Sign in to your compliance dashboard">
    <div className="space-y-4">
      <GoogleButton onClick={loginWithGoogle} testid="login-google" />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" required />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" required />
        <div className="-mt-2 text-right">
          <Link to="/forgot-password" className="text-sm text-sky-600 hover:underline" data-testid="login-forgot-password">Forgot password?</Link>
        </div>
        <button disabled={loading} data-testid="login-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 disabled:opacity-50">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in with email
        </button>
        <p className="text-sm text-center text-slate-600">No account? <Link to="/signup" className="text-sky-600 hover:underline" data-testid="login-to-signup">Create one</Link></p>
      </form>
    </div>
  </AuthShell>;
}

export function Signup() {
  const { signup, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success("Account created — welcome!");
      nav("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Signup failed"));
    } finally { setLoading(false); }
  };

  return <AuthShell title="Create your account" subtitle="Track every STR regulation, avoid the fines">
    <div className="space-y-4">
      <GoogleButton onClick={loginWithGoogle} testid="signup-google" />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Field label="Full name" value={name} onChange={setName} testid="signup-name" required />
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="signup-email" required />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="signup-password" required minLength={6} />
        <button disabled={loading} data-testid="signup-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 disabled:opacity-50">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
        </button>
        <p className="text-sm text-center text-slate-600">Have an account? <Link to="/login" className="text-sky-600 hover:underline" data-testid="signup-to-login">Sign in</Link></p>
      </form>
    </div>
  </AuthShell>;
}

const PRIMARY_BTN = "w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 disabled:opacity-50";

function BackToSignIn() {
  return <p className="text-sm text-center text-slate-600"><Link to="/login" className="text-sky-600 hover:underline" data-testid="back-to-signin">Back to sign in</Link></p>;
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Something went wrong. Please try again."));
    } finally { setLoading(false); }
  };

  if (sent) {
    return <AuthShell title="Check your inbox" subtitle="If that email is registered, a reset link is on its way.">
      <div className="space-y-6" data-testid="forgot-password-sent">
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <MailCheck className="w-5 h-5 flex-shrink-0" />
          <span>The link expires in 1 hour. Don't see it? Check your spam folder or try again in a few minutes.</span>
        </div>
        <BackToSignIn />
      </div>
    </AuthShell>;
  }

  return <AuthShell title="Forgot your password?" subtitle="Enter your account email and we'll send you a reset link.">
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
      <Field label="Email" type="email" value={email} onChange={setEmail} testid="forgot-password-email" required />
      <button disabled={loading} data-testid="forgot-password-submit" className={PRIMARY_BTN}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
      </button>
      <BackToSignIn />
    </form>
  </AuthShell>;
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Could not reset password"));
    } finally { setLoading(false); }
  };

  if (!token) {
    return <AuthShell title="Reset link missing" subtitle="Open the link from your email, or request a new one.">
      <div className="space-y-4" data-testid="reset-password-missing">
        <Link to="/forgot-password" className={PRIMARY_BTN} data-testid="reset-request-new">Request a new link</Link>
        <BackToSignIn />
      </div>
    </AuthShell>;
  }

  return <AuthShell title="Choose a new password" subtitle="Pick something at least 6 characters long.">
    <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
      <Field label="New password" type="password" value={password} onChange={setPassword} testid="reset-password-new" required minLength={6} />
      <Field label="Confirm password" type="password" value={confirm} onChange={setConfirm} testid="reset-password-confirm" required minLength={6} />
      <button disabled={loading} data-testid="reset-password-submit" className={PRIMARY_BTN}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Update password
      </button>
      <BackToSignIn />
    </form>
  </AuthShell>;
}

function GoogleButton({ onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="w-full flex items-center justify-center gap-3 py-3 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium hover:border-slate-400 hover:bg-slate-50 transition">
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62Z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/>
      </svg>
      Continue with Google
    </button>
  );
}

function Divider() {
  return (
    <div className="relative py-2">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
      <div className="relative flex justify-center"><span className="bg-white px-3 text-[10px] font-mono uppercase tracking-widest text-slate-500">or with email</span></div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-widest text-slate-600">{label}</span>
      <input {...rest} type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full px-4 py-3 rounded-lg border border-slate-300 bg-white focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition text-slate-900" />
    </label>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-slate-50 grain flex">
      <div className="hidden lg:flex flex-1 bg-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <span className="font-display font-bold text-lg">HavenGuard</span>
          </Link>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          <h2 className="font-display text-4xl font-bold leading-tight">STR compliance, without the legal team.</h2>
          <p className="text-slate-300">Six US markets. Every rule cited. AI that reads the ordinance so you don't have to.</p>
        </div>
        <div className="relative z-10 text-xs text-slate-500 font-mono">© 2026 · Not legal advice</div>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #0ea5e9 0%, transparent 40%), radial-gradient(circle at 70% 80%, #d97706 0%, transparent 40%)" }} />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-white" /></div>
            <span className="font-display font-bold text-lg">HavenGuard</span>
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{title}</h1>
          <p className="text-slate-600 mt-2 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
